import { useState } from 'react';
import { Bell, Brain, FileText, ListChecks, ShieldCheck, Wrench } from 'lucide-react';
import ApprovalQueue from '../ApprovalQueue';
import DocumentsPanel from './DocumentsPanel';
import MemoryPanel from './MemoryPanel';
import OperatorInbox from './OperatorInbox';
import TaskTimeline from './TaskTimeline';
import ToolCatalogPanel from './ToolCatalogPanel';
import type { AgentTask } from '../../lib/api';

export type RailTab = 'tasks' | 'inbox' | 'approvals' | 'memory' | 'docs' | 'tools';

const TABS: Array<{ id: RailTab; label: string; icon: typeof ListChecks }> = [
  { id: 'tasks', label: 'Tasks', icon: ListChecks },
  { id: 'inbox', label: 'Inbox', icon: Bell },
  { id: 'approvals', label: 'Approvals', icon: ShieldCheck },
  { id: 'memory', label: 'Memory', icon: Brain },
  { id: 'docs', label: 'Docs', icon: FileText },
  { id: 'tools', label: 'Tools', icon: Wrench },
];

const VALID_TABS = new Set<string>(TABS.map((t) => t.id));

export function parseRailTab(value: string | null | undefined): RailTab | null {
  if (!value || !VALID_TABS.has(value)) return null;
  return value as RailTab;
}

interface ContextRailProps {
  tasks: AgentTask[];
  activeTaskId?: string | null;
  tab?: RailTab;
  onTabChange?: (tab: RailTab) => void;
}

export default function ContextRail({
  tasks,
  activeTaskId,
  tab: controlledTab,
  onTabChange,
}: ContextRailProps) {
  const [internalTab, setInternalTab] = useState<RailTab>('tasks');
  const tab = controlledTab ?? internalTab;

  const setTab = (next: RailTab) => {
    onTabChange?.(next);
    if (controlledTab === undefined) setInternalTab(next);
  };

  return (
    <aside className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zt-border bg-zt-surface">
      <div className="flex shrink-0 border-b border-zt-border">
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = id === tab;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              title={label}
              className={`relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] transition ${
                active ? 'text-zt-accent' : 'text-zt-muted hover:text-slate-200'
              }`}
            >
              <Icon size={15} />
              {label}
              {active && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-zt-primary" />
              )}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === 'tasks' && (
          <div className="h-full overflow-y-auto p-3">
            <TaskTimeline tasks={tasks} activeTaskId={activeTaskId} />
          </div>
        )}
        {tab === 'inbox' && (
          <div className="h-full overflow-y-auto">
            <OperatorInbox compact />
          </div>
        )}
        {tab === 'approvals' && (
          <div className="h-full overflow-y-auto p-3">
            <ApprovalQueue compact />
          </div>
        )}
        {tab === 'memory' && <MemoryPanel />}
        {tab === 'docs' && <DocumentsPanel />}
        {tab === 'tools' && <ToolCatalogPanel />}
      </div>
    </aside>
  );
}
